import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faTimes,
  faSave,
  faTrash,
  faCheck,
  faEdit,
  faImages,
} from "@fortawesome/free-solid-svg-icons";
import MobileDropdownModal from "../Common/MobileDropdownModal";
import { Modal, Carousel } from "react-bootstrap";
import axiosUserInstance from "../../axiosUserInstance";
import { useLoader } from "../../context/LoaderContext";
import { uploadFileToS3 } from "../../utils/common";

export default function AlbumsWidgetSheet({
  isOpen,
  onClose,
  widget,
  userData,
  setUserData,
}) {
  const { setLoading } = useLoader();

  // create-album form
  const [newAlbumName, setNewAlbumName] = useState("");
  const [newAlbumMedia, setNewAlbumMedia] = useState([]);
  const fileInputRef = useRef(null);
  const [allAlbums, setAllAlbums] = useState([]);
  const [selectedAlbumIds, setSelectedAlbumIds] = useState(
    Array.isArray(widget?.albumIds) ? widget.albumIds.map(String) : [],
  );
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [viewingAlbum, setViewingAlbum] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    const loadAlbums = async () => {
      try {
        setLoading(true);
        const res = await axiosUserInstance.get("/albums/getAll");
        if (res?.status === 200) {
          const list = Array.isArray(res.data?.data) ? res.data.data : [];
          setAllAlbums(list);
          setUserData((prev) => ({
            ...prev,
            albums: list,
          }));
        }
      } catch (e) {
        // Fallback to whatever is already on userData
        setAllAlbums(Array.isArray(userData?.albums) ? userData.albums : []);
      } finally {
        setLoading(false);
      }
    };
    loadAlbums();
    setSelectedAlbumIds(
      Array.isArray(widget?.albumIds) ? widget.albumIds.map(String) : [],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, widget?._id]);

  const toggleAlbumSelection = (albumId) => {
    const id = String(albumId);
    setSelectedAlbumIds((prev) => {
      const set = new Set(prev.map(String));
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return Array.from(set);
    });
  };

  const startEditingAlbum = (album) => {
    if (!album?._id) return;
    setEditingAlbum(album);
    setNewAlbumName(album.name || "");
    setNewAlbumMedia([]);
  };

  const handleDeleteAlbum = async (album) => {
    if (!album?._id) return;
    // const confirmed = window.confirm(
    //   "Are you sure you want to delete this album for all widgets?",
    // );
    // if (!confirmed) return;
    try {
      setLoading(true);
      const res = await axiosUserInstance.delete(`/albums/delete/${album._id}`);
      if (res?.status === 200) {
        const idStr = String(album._id);
        setAllAlbums((prev) =>
          (prev || []).filter((a) => String(a?._id) !== idStr),
        );
        setUserData((prev) => ({
          ...prev,
          albums: (prev.albums || []).filter((a) => String(a?._id) !== idStr),
        }));
        setSelectedAlbumIds((prev) =>
          (prev || []).filter((id) => String(id) !== idStr),
        );
        if (editingAlbum && String(editingAlbum._id) === idStr) {
          setEditingAlbum(null);
          setNewAlbumName("");
        }
        toast.success("Album deleted successfully.");
      } else {
        toast.error(res?.message || "Failed to delete album.");
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e?.message || "Failed to delete album.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) {
      toast.error("Please upload an image or video.");
      return;
    }
    const tempId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const localPreviewUrl = URL.createObjectURL(file);
    // Store file locally for later upload
    setNewAlbumMedia((prev) => [
      ...(prev || []),
      {
        _tempId: tempId,
        url: localPreviewUrl,
        uploadedUrl: "",
        name: file.name || "",
        size: file.size || 0,
        type: file.type,
        file: file, // Store the actual file object
        isUploading: false, // No immediate upload
      },
    ]);
  };

  const saveAlbum = async () => {
    // Edit existing album name only
    if (editingAlbum && editingAlbum._id) {
      const trimmed = (newAlbumName || "").trim();
      if (!trimmed) {
        toast.error("Album name cannot be empty.");
        return;
      }
      try {
        setLoading(true);
        const res = await axiosUserInstance.put(
          `/albums/update/${editingAlbum._id}`,
          { name: trimmed },
        );
        if (res?.status === 200) {
          const updated = res.data?.data;
          setAllAlbums((prev) =>
            (prev || []).map((a) =>
              String(a?._id) === String(updated?._id) ? updated : a,
            ),
          );
          setUserData((prev) => ({
            ...prev,
            albums: (prev.albums || []).map((a) =>
              String(a?._id) === String(updated?._id) ? updated : a,
            ),
          }));
          toast.success("Album name updated.");
          setEditingAlbum(null);
          setNewAlbumName("");
        } else {
          toast.error(res?.message || "Failed to update album name.");
        }
      } catch (e) {
        toast.error(
          e?.response?.data?.message ||
            e?.message ||
            "Failed to update album name.",
        );
      } finally {
        setLoading(false);
      }
      return;
    }

    // Create new album with media
    if (!newAlbumMedia?.length) {
      toast.error("Please add at least one media item.");
      return;
    }

    // Upload all files to S3 first
    const uploadedMedia = [];
    try {
      setLoading(true);
      for (const media of newAlbumMedia) {
        if (media.file) {
          const uploadedUrl = await uploadFileToS3(media.file, true);
          uploadedMedia.push({
            url: uploadedUrl,
            name: media.name || "",
            size: media.size || 0,
            type: media.type,
          });
          // Clean up the local preview URL
          try {
            URL.revokeObjectURL(media.url);
          } catch (e) {}
        }
      }
    } catch (e) {
      toast.error("Failed to upload media files.");
      return;
    } finally {
      setLoading(false);
    }

    try {
      setLoading(true);
      const res = await axiosUserInstance.post("/albums/create", {
        name: newAlbumName || "",
        media: uploadedMedia,
      });
      if (res?.status === 201) {
        const created = res.data?.data;
        toast.success("Album saved.");
        if (created?._id) {
          setUserData((prev) => ({
            ...prev,
            albums: [created, ...(prev.albums || [])],
          }));
          setAllAlbums((prev) => [created, ...(prev || [])]);
          const updatedIds = [
            ...new Set([...selectedAlbumIds, String(created._id)]),
          ];
          setSelectedAlbumIds(updatedIds);

          // Persist selection to this specific widget
          if (widget?._id) {
            try {
              const widgetRes = await axiosUserInstance.post("/widget/edit", {
                _id: widget._id,
                albumIds: updatedIds,
              });
              if (widgetRes?.status === 200) {
                const updatedWidgetDoc = widgetRes.data?.data;
                setUserData((prev) => ({
                  ...prev,
                  userWidget: updatedWidgetDoc,
                }));
              }
            } catch (e) {
              // If widget update fails, keep local state but notify
              toast.error(
                e?.response?.data?.message ||
                  e?.message ||
                  "Failed to attach album to widget.",
              );
            }
          }
        }
        setNewAlbumName("");
        setNewAlbumMedia([]);
      } else {
        toast.error(res?.message || "Failed to create album.");
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e?.message || "Failed to create album.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <MobileDropdownModal
      isOpen={isOpen}
      onClose={onClose}
      title="Albums"
      className="widget-menu-modal"
      userData={userData}
      disableOutsideClick={!!viewingAlbum}
    >
      <div className="mb-3">
        <div className="form-group">
          <label className="form-label">
            {editingAlbum ? "Edit album name" : "Album name (optional)"}
          </label>
          <input
            className="form-control"
            value={newAlbumName}
            onChange={(e) => setNewAlbumName(e.target.value)}
          />
          <small
            style={{
              display: "block",
              marginTop: 4,
              fontSize: 12,
              color: "#6b7280",
              fontFamily: userData?.pageTheme?.pageFont,
            }}
          >
            {editingAlbum
              ? "Update the name for this album."
              : "Name is optional, but you must add at least one photo or video."}
          </small>
        </div>

        {!editingAlbum && (
          <div className="form-group">
            <label className="form-label">Add media</label>
            <div
              className="file-upload"
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
            >
              <FontAwesomeIcon icon={faUpload} />
              <p>Click to upload media</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                style={{ display: "none" }}
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  files.forEach(file => handleUpload(file));
                  // Reset input value to allow re-selecting same files
                  e.target.value = '';
                }}
              />
            </div>

            {newAlbumMedia?.length > 0 && (
              <div className="mt-2 d-flex flex-column gap-2">
                {newAlbumMedia.map((m, idx) => (
                  <div
                    key={m?._tempId || `${m.url}-${idx}`}
                    className="d-flex align-items-center justify-content-between border rounded p-2"
                  >
                    <div
                      className="d-flex align-items-center gap-2"
                      style={{ maxWidth: "82%" }}
                    >
                      {m?.type?.startsWith("video/") ? (
                        <div
                          style={{
                            width: 54,
                            height: 54,
                            borderRadius: 10,
                            overflow: "hidden",
                            background: "#f3f4f6",
                            flex: "0 0 auto",
                          }}
                        >
                          <video
                            src={m.url}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            muted
                            playsInline
                          />
                        </div>
                      ) : (
                        <img
                          src={m.url}
                          alt={m.name || "media"}
                          style={{
                            width: 54,
                            height: 54,
                            borderRadius: 10,
                            objectFit: "cover",
                            background: "#f3f4f6",
                            flex: "0 0 auto",
                          }}
                        />
                      )}
                      <div className="text-truncate">
                        <span
                          style={{ fontFamily: userData?.pageTheme?.pageFont }}
                        >
                          {m.name || "Media"}
                        </span>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          {m.type}
                        </div>
                      </div>
                    </div>
                    <button
                      className="btn btn-sm btn-light"
                      type="button"
                      onClick={() =>
                        setNewAlbumMedia((prev) =>
                          (prev || []).filter((_, i) => i !== idx),
                        )
                      }
                      title="Remove"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="d-flex gap-2 mt-2">
          <button
            className="btn btn-sm btn-primary"
            type="button"
            onClick={saveAlbum}
            disabled={
              (!editingAlbum &&
                !newAlbumMedia?.length) ||
              false
            }
          >
            <FontAwesomeIcon icon={faSave} />{" "}
            {editingAlbum ? "Save name" : "Save album"}
          </button>
          {editingAlbum && (
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={() => {
                setEditingAlbum(null);
                setNewAlbumName("");
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {allAlbums.length > 0 && (
        <>
          <hr />
          <div className="mb-2">
            <h6
              style={{
                fontFamily: userData?.pageTheme?.pageFont,
                marginBottom: 8,
              }}
            >
              Albums for this widget
            </h6>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 8,
              }}
            >
              {allAlbums.map((album) => {
                const first = album?.media?.[0];
                if (!first) return null;
                const isSelected = selectedAlbumIds.includes(
                  String(album?._id),
                );
                return (
                  <div key={album?._id}>
                    <div
                      onClick={() => toggleAlbumSelection(album?._id)}
                      style={{
                        width: "100%",
                        paddingTop: "100%",
                        position: "relative",
                        borderRadius: 10,
                        overflow: "hidden",
                        background: isSelected ? "#dbeafe" : "#f3f4f6",
                        cursor: "pointer",
                        border: isSelected ? `3px solid ${
                              userData?.pageTheme?.buttonBorder || "#6c5ce7"
                          }` : "3px solid transparent",
                      }}
                    >
                      {first.type?.startsWith("video/") ? (
                        <video
                          src={first.url}
                          style={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          muted
                          playsInline
                        />
                      ) : (
                        <img
                          src={first.url}
                          alt={album?.name || "album"}
                          style={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      )}
                      {/* custom styled checkbox using theme values (visible always) */}
                      <div
                        style={{
                          position: "absolute",
                          top: 6,
                          right: 6,
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          background: userData?.pageTheme?.buttonBackground,
                          border: `2px solid ${
                            userData?.pageTheme?.buttonBorder || "#6c5ce7"
                          }`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          zIndex: 2,
                        }}
                        data-tooltip-id="my-tooltip"
                        data-tooltip-place="left"
                        data-tooltip-content={isSelected ? "Deselect album" : "Select album"}
                      >
                        {isSelected && (
                          <FontAwesomeIcon
                            icon={faCheck}
                            style={{
                              color:
                                userData?.pageTheme?.buttonBorder ||
                                "#6c5ce7",
                              fontSize: 12,
                            }}
                          />
                        )}
                      </div>
                    </div>
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 11,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 4,
                        fontFamily: userData?.pageTheme?.pageFont,
                      }}
                    >
                      <span className="text-truncate">
                        {album?.name || "Untitled album"}
                      </span>
                      <div className="d-flex align-items-center gap-1">
                        <button
                          type="button"
                          onClick={() => startEditingAlbum(album)}
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "#2563eb",
                            cursor: "pointer",
                            padding: 0,
                            fontSize: 11,
                          }}
                          data-tooltip-id="my-tooltip"
                          data-tooltip-place="top"
                          data-tooltip-content={"Edit album name"}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setViewingAlbum(album)}
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "#059669",
                            cursor: "pointer",
                            padding: 0,
                            fontSize: 11,
                          }}
                          data-tooltip-id="my-tooltip"
                          data-tooltip-place="top"
                          data-tooltip-content={"View all media"}
                        >
                          <FontAwesomeIcon icon={faImages} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteAlbum(album)}
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "#dc2626",
                            cursor: "pointer",
                            padding: 0,
                            fontSize: 11,
                          }}
                          data-tooltip-id="my-tooltip"
                          data-tooltip-place="top"
                          data-tooltip-content={"Delete this album"}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 d-flex justify-content-between align-items-center">
              {widget?._id && (
                <button
                  className="btn btn-sm btn-primary"
                  type="button"
                  onClick={async () => {
                    try {
                      setLoading(true);
                      const res = await axiosUserInstance.post("/widget/edit", {
                        _id: widget._id,
                        albumIds: selectedAlbumIds,
                      });
                      if (res?.status === 200) {
                        const updatedDoc = res.data?.data;
                        setUserData((prev) => ({
                          ...prev,
                          userWidget: updatedDoc,
                        }));
                        toast.success("Albums updated for this widget.");
                      } else {
                        toast.error(
                          res?.message || "Failed to update widget albums.",
                        );
                      }
                    } catch (e) {
                      toast.error(
                        e?.response?.data?.message ||
                          e?.message ||
                          "Failed to update widget albums.",
                      );
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faSave} /> Save selection
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* Albums Media Viewer Modal */}
      <Modal
        show={!!viewingAlbum}
        onHide={() => setViewingAlbum(null)}
        centered
        size="lg"
        backdrop="static"
        className="album-viewer-modal"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Modal.Header closeButton className="px-0 pt-0 mb-3">
          <Modal.Title
            style={{ fontFamily: userData?.pageTheme?.pageFont }}
            className="mb-0"
          >
            {viewingAlbum?.name || "Album"} ({viewingAlbum?.media?.length || 0}
            {" "} items)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {viewingAlbum?.media?.length > 0 ? (
            <div style={{ position: "relative" }}>
              <Carousel
                interval={null}
                nextIcon={
                  <span
                    style={{
                      background: "rgba(0, 0, 0, 0.7)",
                      borderRadius: "50%",
                      width: "40px",
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "18px",
                      border: "2px solid rgba(255, 255, 255, 0.3)",
                    }}
                  >
                    ›
                  </span>
                }
                prevIcon={
                  <span
                    style={{
                      background: "rgba(0, 0, 0, 0.7)",
                      borderRadius: "50%",
                      width: "40px",
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "18px",
                      border: "2px solid rgba(255, 255, 255, 0.3)",
                    }}
                  >
                    ‹
                  </span>
                }
              >
                {viewingAlbum.media.map((m, idx) => (
                  <Carousel.Item key={idx}>
                    {m?.type?.startsWith("video/") ? (
                      <video
                        src={m?.url}
                        style={{
                          width: "100%",
                          height: "auto",
                          maxHeight: "500px",
                          objectFit: "contain",
                          background: "#f3f4f6",
                        }}
                        controls
                        muted
                        playsInline
                      />
                    ) : (
                      <img
                        src={m.url}
                        alt={`${viewingAlbum?.name || "Album"} ${idx + 1}`}
                        style={{
                          width: "100%",
                          height: "auto",
                          maxHeight: "500px",
                          objectFit: "contain",
                          background: "#f3f4f6",
                        }}
                      />
                    )}
                  </Carousel.Item>
                ))}
              </Carousel>
            </div>
          ) : (
            <div
              style={{
                padding: "40px",
                textAlign: "center",
                fontFamily: userData?.pageTheme?.pageFont,
              }}
            >
              No media available in this album.
            </div>
          )}
        </Modal.Body>
      </Modal>
    </MobileDropdownModal>
  );
}
